// UI adaptation of B3 commands. No prices, costs, margins or tax are computed here.
const b1 = require('./b1');
const { scopeKey } = require('./b1/contracts');
export const sameScope = (a, b) => scopeKey(a) === scopeKey(b);
export const subtree = (graph, id) => {
  const nodes = new Map(graph.nodes.map(n => [n.id, n]));
  const ids = [], pending = [String(id)];
  while (pending.length) { const current = pending.pop(); ids.push(current); pending.push(...(nodes.get(current)?.childIds || [])); }
  return ids;
};
export const sourceFor = (model, entry) => ({ quoteVersionId: model.quoteVersionId, scope: entry.scope,
  sections: entry.sections.map(s => ({ ...s, quoteVersionId: model.quoteVersionId, scope: entry.scope })),
  lines: entry.lines.map(l => ({ ...l, quantity: l.localQuantity ?? l.quantity, quoteVersionId: model.quoteVersionId, scope: entry.scope })) });
export function preflight(model, entry, command) {
  const fail = (code, message) => ({ valid: false, errors: [{ code, message }] });
  if (model.operational && ['addOption', 'patchOption', 'removeOption'].includes(command.type)) return { valid: true, errors: [] };
  if (command.quoteVersionId && command.quoteVersionId !== model.quoteVersionId)
    return fail('QUOTE_COMPOSITION_VERSION_MISMATCH', 'Déplacement vers une autre version interdit.');
  const before = sourceFor(model, entry), candidate = { ...before, lines: before.lines.map(l => ({ ...l })) };
  const apply = c => {
    if (!sameScope(entry.scope, c.scope)) throw Object.assign(new Error(), { code: 'QUOTE_COMPOSITION_SCOPE_MISMATCH', message: 'Déplacement entre principal et option interdit.' });
    const line = candidate.lines.find(l => String(l.id) === String(c.lineId));
    switch (c.type) {
      case 'batch': c.mutations.forEach(apply); break;
      case 'patchLine': Object.assign(line, c.patch); break;
      case 'forcePrice': Object.assign(line, { sellingPriceMode: 'forced', forcedUnitSellingPrice: c.value }); break;
      case 'resetPrice': Object.assign(line, { sellingPriceMode: 'calculated', forcedUnitSellingPrice: null }); break;
      case 'reparent': {
        const target = candidate.lines.find(l => l.id === c.parentLineId);
        if (!entry.sections.some(s => s.id === c.sectionId) || (target && target.quoteSectionId !== c.sectionId))
          throw Object.assign(new Error(), { code: 'QUOTE_COMPOSITION_SECTION_MISMATCH', message: 'Le parent et ses prestations doivent partager la même section.' });
        const checked = b1.validateHierarchy(candidate);
        if (!checked.valid) throw checked.errors[0];
        const ids = new Set(subtree(checked.graph, c.lineId));
        candidate.lines.forEach(l => { if (ids.has(l.id)) l.quoteSectionId = c.sectionId; });
        line.parentLineId = c.parentLineId; break;
      }
      case 'addLine': candidate.lines.push({ ...c.line, id: c.id, quoteSectionId: c.sectionId,
        quoteVersionId: model.quoteVersionId, scope: entry.scope, sellingPriceMode: 'calculated' }); break;
      case 'removeSubtree': candidate.lines = candidate.lines.filter(l => !c.lineIds.includes(l.id)); break;
      case 'calculate': break;
      case 'forceCoefficient': case 'resetCoefficient': case 'duplicateLine': case 'addCatalog': case 'addCatalogLine': case 'importDpgf': case 'importPlanning':
      case 'addSection': case 'patchSection': case 'removeSection': case 'addOption': case 'patchOption': case 'removeOption': case 'patchScope':
        // These commands are authorized and validated transactionally by B3/B6.
        // Never simulate acceptance or import source economics in the browser.
        if (!model.operational) throw Object.assign(new Error(), { code: 'QUOTE_COMPOSITION_INVALID', message: 'Commande serveur requise.' });
        break;
      default: throw Object.assign(new Error(), { code: 'QUOTE_COMPOSITION_INVALID', message: 'Commande indisponible.' });
    }
  };
  try { apply(command); return b1.validateVatMutation(before, candidate); }
  catch (error) { return fail(error.code || 'QUOTE_COMPOSITION_INVALID', error.message || 'Commande invalide.'); }
}
export const moveSibling = (entry, graph, line, direction) => {
  const siblings = graph.nodes.filter(n => n.parentLineId === line.parentLineId && n.quoteSectionId === line.quoteSectionId);
  const index = siblings.findIndex(n => n.id === line.id), next = index + direction;
  if (next < 0 || next >= siblings.length) return null;
  [siblings[index], siblings[next]] = [siblings[next], siblings[index]];
  return { type: 'batch', scope: entry.scope, mutations: siblings.map((n, i) => ({ type: 'patchLine', scope: entry.scope, lineId: n.id, patch: { orderIndex: i } })) };
};
export const readableError = error => /MIXED_VAT|FORCED_VAT_CONFLICT/.test(error?.code || '')
  ? 'Modification refusée : impossible de fixer un prix global à cet ensemble ; ses prestations utilisent plusieurs taux de TVA. La valeur acceptée reste inchangée.'
  : error?.message || 'Opération refusée.';
